import React from 'react';
import { Button } from '@/components/ui/button';
import { Download, FileText, FileImage, File, Eye } from 'lucide-react';

interface FileData {
    name: string;
    size: string;
    type: string;
    extension: string;
    is_image: boolean;
    is_pdf: boolean;
    created_at: string;
    url: string | null;
    download_url: string;
}

interface ShareInfo {
    access_count: number;
    expires_at: string | null;
}

interface Props {
    file: FileData;
    share_info: ShareInfo;
}

export default function FileView({ file, share_info }: Props) {
    const getFileIcon = () => {
        if (file.is_image) return <FileImage className="h-16 w-16 text-blue-500" />;
        if (file.is_pdf) return <FileText className="h-16 w-16 text-red-500" />;
        return <File className="h-16 w-16 text-muted-foreground" />;
    };

    const handleDownload = () => {
        window.location.href = file.download_url;
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-6">
            <div className="w-full max-w-3xl">
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-white">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="bg-white/20 p-3 rounded-lg">
                                {getFileIcon()}
                            </div>
                            <div className="flex-1">
                                <h1 className="text-2xl font-bold mb-1">{file.name}</h1>
                                <p className="text-blue-100">{file.size}</p>
                            </div>
                        </div>
                    </div>

                    {/* Preview */}
                    {file.is_image && file.url && (
                        <div className="p-6 bg-slate-50 dark:bg-slate-900">
                            <img
                                src={file.url}
                                alt={file.name}
                                className="max-w-full h-auto rounded-lg shadow-lg mx-auto"
                            />
                        </div>
                    )}

                    {file.is_pdf && file.url && (
                        <div className="p-6 bg-slate-50 dark:bg-slate-900">
                            <iframe
                                src={file.url}
                                className="w-full h-[600px] rounded-lg shadow-lg"
                                title={file.name}
                            />
                        </div>
                    )}

                    {/* Actions */}
                    <div className="p-6 space-y-4">
                        <Button
                            onClick={handleDownload}
                            className="w-full h-12 text-lg"
                            size="lg"
                        >
                            <Download className="h-5 w-5 mr-2" />
                            Download File
                        </Button>

                        {/* File Info */}
                        <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                            <div>
                                <p className="text-sm text-muted-foreground">Type</p>
                                <p className="font-medium">{file.extension.toUpperCase() || 'File'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Size</p>
                                <p className="font-medium">{file.size}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Uploaded</p>
                                <p className="font-medium">{file.created_at}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Views</p>
                                <p className="font-medium flex items-center gap-1">
                                    <Eye className="h-4 w-4" />
                                    {share_info.access_count}
                                </p>
                            </div>
                        </div>

                        {share_info.expires_at && (
                            <div className="text-center text-sm text-muted-foreground">
                                This link expires on {new Date(share_info.expires_at).toLocaleDateString()}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="text-center mt-6 text-sm text-muted-foreground">
                    <p>This file was shared securely</p>
                </div>
            </div>
        </div>
    );
}